import { useEffect, useRef, useState } from 'react'
import type { Dispatch, RefObject, SetStateAction } from 'react'

import { useSession } from 'next-auth/react'
import Image from 'next/image'
import Link from 'next/link'

import { useIntersection, useWindowSize } from 'react-use'

import profileContent from '@data/i18n/profile.json'

import { prodUrl } from '@lib/constants'
import useUpdateShopifyFavorites from '@lib/hooks/useUpdateShopifyFavorites'

import StarRatings from '@components/StarRatings'
import AddToCart from '@components/product/ProductView/AddToCart'
import NoProductImage from '@components/products/NoProductImage'
import ProductUnavailable from '@components/products/ProductUnavailable'

import ReadMoreTruncatedText from './ReadMoreTruncatedText'

type PastBoxesProductCardProps = {
	// TODO: product type
	product: any
	index: number
	activeItemIndex: number
	setActiveItemIndex: Dispatch<SetStateAction<number>>
	intersectionRoot: RefObject<HTMLDivElement>
	className?: string
	isFavorite?: boolean
}
export default function PastBoxesProductCard({
	product,
	index,
	intersectionRoot,
	activeItemIndex,
	setActiveItemIndex,
	className = '',
	isFavorite = false
}: PastBoxesProductCardProps) {
	const { data: session } = useSession()
	const {
		valid,
		handle,
		imageUrl,
		fullSizeVariantId,
		title,
		vendor,
		description,
		howToUse,
		price,
		recommendationTags,
		avgRating,
		numReviews,
		quantityAvailable,
		availableForSale,
		isProductAvailableForSale
	} = product || {}

	const [favoriteProduct, setFavoriteProduct] = useState(isFavorite)
	const updateShopifyFavorites = useUpdateShopifyFavorites()
	const [loading, setLoading] = useState(false)

	const ref = useRef<HTMLDivElement>(null)

	const { isIntersecting } =
		useIntersection(ref, {
			root: intersectionRoot?.current,
			rootMargin: '0px',
			threshold: 0.75
		}) || {}

	const { width } = useWindowSize()

	useEffect(() => {
		if (width < 401 && isIntersecting) {
			setActiveItemIndex(index)
		}
	}, [isIntersecting, width])

	useEffect(() => {
		setFavoriteProduct(isFavorite)
	}, [isFavorite])

	const handleFavorite = () => {
		if (session) {
			const currentFavorite = favoriteProduct
			// FYI -- product.id is the product variant id and not the actual product id
			setFavoriteProduct(!favoriteProduct)
			updateShopifyFavorites(product.productId, !currentFavorite ? 'add' : 'remove')
		}
	}

	return (
		<div
			className={`relative flex flex-col justify-between shrink-0 snap-center snap-always px-8 pt-8 pb-12 bg-white border rounded-sm w-[364px] max-sm:w-[75%] transition duration-200 ease-in-out ${className} ${
				activeItemIndex === index
					? 'max-xs:scale-y-100 border-bbx-spark'
					: 'max-xs:scale-y-75 border-gray-200'
			}`}
			ref={ref}
		>
			{valid ? (
				<div className="divide-y divide-gray-200">
					{/* <Link href={`${prodUrl}/${handle}`}>
						<a> */}
							<div className="flex pb-6 gap-x-4 max-sm:block max-sm:text-center">
								{/* Product image */}
								{imageUrl ? (
									<div className="shrink-0">
										<Image
											src={imageUrl}
											alt={title}
											width={120}
											height={120}
											className="object-contain"
										/>
									</div>
								) : (
									<div className="md:mt-6 md:ml-4">
										<NoProductImage classNames="p-6 bg-gray-100" iconSize="fa-4x" />
									</div>
								)}

								<div className="flex flex-col justify-center mr-4 gap-y-2 max-sm:mr-0">
									{/* Product vendor */}
									{vendor && <h3 className="text-sm font-medium">{vendor}</h3>}

									{/* Product title */}
									{title && <p className="text-xs font-light">{title}</p>}

									{/* Star ratings */}
									{avgRating?.value && (
										<div className="flex items-center gap-x-1.5">
											<StarRatings avgRating={parseInt(avgRating.value, 10)} />

											{numReviews?.value && (
												<span className="text-xs font-medium leading-none">{numReviews.value}</span>
											)}
										</div>
									)}
								</div>
							</div>
						{/* </a>
					</Link> */}

					{/* Full size price and add to cart */}
					{price && parseFloat(price) > 0 && (
						<div className="flex items-center justify-between py-3.5">
							<div className="flex flex-col justify-center text-xs">
								<span className="font-medium">
									{profileContent.pastBoxProducts.card.fullSizePriceLabel}
								</span>

								<span className="text-gray-500">${price}</span>
							</div>

							<AddToCart
								product={{ availableForSale: isProductAvailableForSale }}
								variant={{ id: fullSizeVariantId, availableForSale, quantityAvailable }}
								loading={loading}
								setLoading={setLoading}
								sellingPlanId={null}
								ctaText={profileContent.pastBoxProducts.card.addToCartLabel}
								className="text-sm font-medium uppercase text-bbx-spark"
								renderSoldOutButton={false}
							/>
						</div>
					)}

					{/* Recommendation tags */}
					{Array.isArray(recommendationTags) && recommendationTags.length > 0 && (
						<div className="flex flex-col items-center gap-4 py-6">
							<h4 className="font-serif text-lg">
								{profileContent.pastBoxProducts.card.recommendationTagsLabel}
							</h4>

							<div className="flex flex-wrap justify-center gap-4">
								{recommendationTags
									.filter((tag) => tag)
									?.map((tag, i) => (
										<span
											className="recommendation-tag"
											// eslint-disable-next-line react/no-array-index-key
											key={`${tag}-${i}`}
										>
											{tag}
										</span>
									))}
							</div>
						</div>
					)}

					{/* Overview */}
					{description && (
						<div className="flex flex-col py-6 gap-y-2">
							<h4 className="text-sm font-bold">
								{profileContent.pastBoxProducts.card.overviewLabel}
							</h4>

							<ReadMoreTruncatedText
								text={description}
								ctaLabel={profileContent.pastBoxProducts.card.readMoreTextLabel}
							/>
						</div>
					)}

					{/* Directions */}
					{howToUse?.value && (
						<div className="flex flex-col py-6 gap-y-2">
							<h4 className="text-sm font-bold">
								{profileContent.pastBoxProducts.card.directionsLabel}
							</h4>

							<ReadMoreTruncatedText
								text={howToUse.value}
								ctaLabel={profileContent.pastBoxProducts.card.readMoreTextLabel}
							/>
						</div>
					)}
				</div>
			) : (
				<div>
					<ProductUnavailable />
				</div>
			)}

			{/* CTAs */}
			{/* <div className="flex flex-col mx-5 mt-2 gap-y-4">
				<AddToCart
					product={{ availableForSale: isProductAvailableForSale }}
					variant={{ id: fullSizeVariantId, availableForSale, quantityAvailable }}
					loading={loading}
					setLoading={setLoading}
					sellingPlanId={null}
					ctaText={profileContent.pastBoxProducts.card.addFullSizeToCartLabel}
					className="flex items-center justify-center font-medium button button-primary gap-x-3"
					renderSoldOutButton={false}
					icon="fa-light fa-cart-shopping"
				/>

				{price && price > 0 && (
					<button
						type="button"
						className="flex items-center justify-center font-medium button button-secondary gap-x-3"
						onClick={() => handleFavorite()}
					>
						<i className={`fa-heart ${favoriteProduct ? 'fa-solid' : 'fa-regular'}`} />
						{profileContent.pastBoxProducts.card.addToFavoritesLabel}
					</button>
				)}
			</div> */}
		</div>
	)
}
